import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, Plus, Share2, MessageCircle, Download, Link2, ShieldAlert, CheckCircle2, Eye, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate, today, openFile } from "@/lib/api";
import { Field } from "@/components/common";
import { DocUpload, ExpiryBadge, TripStatus, PAY_MODES, useMarketRoles, nativeInput, WhatsAppPdfShare } from "@/components/market";
import { TripDialog } from "@/pages/MarketTrips";
import { DocHistory } from "@/components/docs";
import { VendorBillCard } from "@/components/vendorBill";

function ShareDialog({ t, open, onClose }) {
  const [info, setInfo] = useState(null);
  useEffect(() => { if (open) { setInfo(null); api.get(`/market/trips/${t.id}/share`).then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e))); } }, [open, t.id]);
  const msg = info && `Market Vehicle Documents — ${t.vehicle_number}\nLR: ${t.lr_number} (${t.route})\nDriver: ${t.driver_name || "-"} ${t.driver_phone ? `(${t.driver_phone})` : ""}\nRC / Insurance / Fitness / PUC / Permit / DL / ID (${info.count} documents):\n${info.url}`;
  const download = async (fmt) => {
    try {
      const r = await api.get(`/market/trips/${t.id}/bundle`, { params: { fmt }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `${t.vehicle_number}_Market_Documents.${fmt}`;
      a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-card" data-testid="market-share-dialog">
        <DialogHeader><DialogTitle>Share All Documents — {t.vehicle_number}</DialogTitle></DialogHeader>
        {!info ? <p className="text-sm text-muted-foreground">Preparing secure link…</p> : (
          <div className="space-y-3">
            <div className="whitespace-pre-line rounded-lg border bg-secondary/50 p-3 text-xs" data-testid="market-share-preview">{msg}</div>
            <a data-testid="market-share-whatsapp-btn" href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
              <MessageCircle className="h-4 w-4" /> Share via WhatsApp
            </a>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" data-testid="market-share-copy-btn" onClick={() => { navigator.clipboard.writeText(info.url); toast.success("Link copied"); }}><Link2 className="mr-1 h-4 w-4" />Copy</Button>
              <Button variant="outline" data-testid="market-share-zip-btn" onClick={() => download("zip")}><Download className="mr-1 h-4 w-4" />ZIP</Button>
              <Button variant="outline" data-testid="market-share-pdf-btn" onClick={() => download("pdf")}><Download className="mr-1 h-4 w-4" />PDF</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SendVendorDialog({ t, open, onClose }) {
  const [info, setInfo] = useState(null);
  useEffect(() => { if (open) { setInfo(null); api.get(`/market/trips/${t.id}/vendor-message`).then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e))); } }, [open, t.id]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-card" data-testid="send-vendor-dialog">
        <DialogHeader><DialogTitle>Send Hire Slip to Vendor</DialogTitle></DialogHeader>
        {info ? <WhatsAppPdfShare info={info} testid="send-vendor" /> : <p className="text-sm text-muted-foreground">Preparing PDF…</p>}
      </DialogContent>
    </Dialog>
  );
}

function ReceiptDialog({ pid, onClose }) {
  const [info, setInfo] = useState(null);
  useEffect(() => { if (pid) { setInfo(null); api.get(`/market/payments/${pid}/receipt-message`).then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e))); } }, [pid]);
  return (
    <Dialog open={!!pid} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-card" data-testid="receipt-dialog">
        <DialogHeader><DialogTitle>Send Payment Receipt</DialogTitle></DialogHeader>
        {info ? <WhatsAppPdfShare info={info} testid="receipt" /> : <p className="text-sm text-muted-foreground">Preparing receipt…</p>}
      </DialogContent>
    </Dialog>
  );
}

const ENTRY_FORMS = {
  shortages: { title: "Shortage / Loss Debit Note", empty: { amount: "", remarks: "", file_id: null, date: today() } },
  detentions: { title: "Detention / Halt Charges", empty: { kind: "Loading", units: "", unit_type: "Days", rate: "", reason: "" } },
  payments: { title: "Vendor Payment", empty: { kind: "advance", amount: "", mode: "Cash", ref_id: "", date: today(), note: "" } },
};

function EntryDialog({ kind, initial, trip, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (kind) setF(initial ? { ...ENTRY_FORMS[kind].empty, ...initial } : ENTRY_FORMS[kind].empty); }, [kind, initial]);
  if (!kind) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, amount: Number(f.amount) || 0, units: Number(f.units) || 0, rate: Number(f.rate) || 0 };
    try {
      const base = kind === "payments" ? (initial ? `/market/payments/${initial.id}` : `/market/trips/${trip.id}/payments`) : `/market/trips/${trip.id}/${kind}${initial ? `/${initial.id}` : ""}`;
      let data;
      try {
        ({ data } = initial ? await api.put(base, body) : await api.post(base, body));
      } catch (err) {
        const kycErr = kind === "payments" && !initial && err.response?.status === 409 && String(err.response?.data?.detail || "").startsWith("KYC pending");
        if (!kycErr || !window.confirm(`⚠ ${errMsg(err)}.\n\nPayment phir bhi record karna hai?`)) throw err;
        ({ data } = await api.post(`${base}?kyc_ack=true`, body));
      }
      toast.success("Saved — net payable updated");
      onSaved(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card" data-testid={`entry-dialog-${kind}`}>
        <DialogHeader><DialogTitle>{initial ? "Edit" : "Add"} {ENTRY_FORMS[kind].title}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
          {kind === "shortages" && <>
            <Field label="Shortage Amount (₹) *"><Input required type="number" min="1" data-testid="shortage-amount-input" value={f.amount} onChange={set("amount")} /></Field>
            <Field label="Date"><Input type="date" data-testid="shortage-date-input" value={f.date} onChange={set("date")} /></Field>
            <Field label="Remarks *" className="sm:col-span-2"><Input required data-testid="shortage-remarks-input" value={f.remarks} onChange={set("remarks")} placeholder="e.g. 2 bags damaged, 150 kg weight short" /></Field>
            <Field label="Proof / Photo (optional)" className="sm:col-span-2"><DocUpload label="Proof" refType="document" value={f.file_id} onChange={set("file_id")} testid="shortage-proof-upload" /></Field>
          </>}
          {kind === "detentions" && <>
            <Field label="Detention At">
              <select data-testid="detention-kind-select" className={nativeInput} value={f.kind} onChange={set("kind")}>{["Loading", "Unloading", "Halting"].map((x) => <option key={x}>{x}</option>)}</select>
            </Field>
            <Field label="Unit">
              <select data-testid="detention-unit-select" className={nativeInput} value={f.unit_type} onChange={set("unit_type")}>{["Days", "Hours"].map((x) => <option key={x}>{x}</option>)}</select>
            </Field>
            <Field label={`No. of ${f.unit_type} *`}><Input required type="number" step="0.5" min="0.5" data-testid="detention-units-input" value={f.units} onChange={set("units")} /></Field>
            <Field label={`Rate per ${f.unit_type === "Days" ? "Day" : "Hour"} (₹) *`}><Input required type="number" min="1" data-testid="detention-rate-input" value={f.rate} onChange={set("rate")} /></Field>
            <Field label="Total Detention (auto)"><div className="flex h-10 items-center rounded-md bg-secondary px-3 text-sm font-semibold amount" data-testid="detention-total">{inr((Number(f.units) || 0) * (Number(f.rate) || 0))}</div></Field>
            <Field label="Reason"><Input data-testid="detention-reason-input" value={f.reason} onChange={set("reason")} /></Field>
          </>}
          {kind === "payments" && <>
            <Field label="Payment Type">
              <select data-testid="payment-kind-select" className={nativeInput} value={f.kind} onChange={set("kind")}><option value="advance">Advance</option><option value="final">Final Balance</option></select>
            </Field>
            <Field label="Amount (₹) *"><Input required type="number" min="1" data-testid="payment-amount-input" value={f.amount} onChange={set("amount")} /></Field>
            <Field label="Mode">
              <select data-testid="payment-mode-select" className={nativeInput} value={f.mode} onChange={set("mode")}>{PAY_MODES.map((x) => <option key={x}>{x}</option>)}</select>
            </Field>
            <Field label="Payment Date *"><Input required type="date" data-testid="payment-date-input" value={f.date} onChange={set("date")} /></Field>
            <Field label="Transaction Ref / Voucher No."><Input data-testid="payment-ref-input" value={f.ref_id} onChange={set("ref_id")} /></Field>
            <Field label="Note"><Input data-testid="payment-note-input" value={f.note} onChange={set("note")} /></Field>
            {!initial && <p className="text-[11px] text-muted-foreground sm:col-span-2">Outstanding before this payment: <b>{inr(trip.totals?.outstanding, 2)}</b></p>}
          </>}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="entry-save-btn" disabled={busy}>Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Card({ title, action, children, testid }) {
  return (
    <div className="card-base p-5" data-testid={testid}>
      <div className="mb-3 flex items-center justify-between gap-2"><h3 className="font-semibold">{title}</h3>{action}</div>
      {children}
    </div>
  );
}

function EntryRows({ items, render, onEdit, onDelete, locked, testid }) {
  if (!items.length) return <p className="text-sm text-muted-foreground">No entries</p>;
  return (
    <div className="divide-y">
      {items.map((x) => (
        <div key={x.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`${testid}-${x.id}`}>
          <div className="min-w-0 flex-1">{render(x)}</div>
          {!locked && <>
            <Button size="icon" variant="ghost" data-testid={`${testid}-edit-${x.id}`} onClick={() => onEdit(x)}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" data-testid={`${testid}-delete-${x.id}`} onClick={() => onDelete(x)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
          </>}
        </div>
      ))}
    </div>
  );
}

function FinanceCard({ t, locked, onEdit }) {
  const c = t.totals;
  const rows = [[t.hire_rate_type === "per_mt" ? `Agreed Market Freight (${t.hire_weight} MT × ${inr(t.hire_rate, 2)})` : "Agreed Market Freight", c.freight, ""], [`TDS (${t.tds_percent}%)`, -c.tds, "text-rose-600"], ["Mamul / Commission", -c.mamul, "text-rose-600"], ["Shortage Debit", -c.shortage, "text-rose-600"], ["Detention Added", c.detention, "text-emerald-600"]];
  return (
    <Card title="Freight & Vendor Payable" testid="market-finance-card" action={!locked && <Button size="sm" variant="outline" data-testid="edit-freight-btn" onClick={onEdit}><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>}>
      <div className="space-y-1.5 text-sm">
        {rows.map(([l, v, cls]) => <div key={l} className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className={`amount ${cls}`}>{v < 0 ? "− " : ""}{inr(Math.abs(v), 2)}</span></div>)}
        <div className="flex justify-between border-t pt-2 font-semibold"><span>Net Payable</span><span className="amount" data-testid="market-net-payable">{inr(c.net_payable, 2)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Advance Paid</span><span className="amount">− {inr(c.advance_paid, 2)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Balance Paid</span><span className="amount">− {inr(c.balance_paid, 2)}</span></div>
        <div className={`flex justify-between rounded-lg px-3 py-2 font-bold ${c.outstanding > 0 ? "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400" : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"}`}>
          <span>Outstanding Payable</span><span className="amount" data-testid="market-outstanding">{inr(c.outstanding, 2)}</span>
        </div>
      </div>
    </Card>
  );
}

function DocsCard({ t, canEdit, onReplace }) {
  const extra = [["aadhaar_front_file_id", "Driver Aadhaar / ID — Front"], ["aadhaar_back_file_id", "Driver Aadhaar / ID — Back"]];
  return (
    <div className="space-y-2">
      {t.docs.map((d) => (
        <div key={d.key} className="flex flex-wrap items-center gap-3 rounded-lg border p-2.5" data-testid={`market-doc-${d.key}`}>
          <div className="w-36 text-sm font-medium">{d.label}</div>
          <ExpiryBadge date={d.expiry} testid={`market-doc-badge-${d.key}`} />
          <div className="flex-1" />
          {canEdit ? <DocUpload label="" value={d.file_id} onChange={(id) => onReplace(`${d.key}_file_id`, id)} testid={`market-doc-replace-${d.key}`} />
            : d.file_id && <button className="text-xs font-semibold text-primary" onClick={() => openFile(d.file_id)}><Eye className="mr-1 inline h-3.5 w-3.5" />View</button>}
        </div>
      ))}
      {extra.map(([f, l]) => (
        <div key={f} className="flex flex-wrap items-center gap-3 rounded-lg border p-2.5">
          <div className="flex-1 text-sm font-medium">{l}</div>
          {canEdit ? <DocUpload label="" value={t[f]} onChange={(id) => onReplace(f, id)} testid={`market-doc-replace-${f}`} />
            : t[f] && <button className="text-xs font-semibold text-primary" onClick={() => openFile(t[f])}><Eye className="mr-1 inline h-3.5 w-3.5" />View</button>}
        </div>
      ))}
    </div>
  );
}

export default function MarketTripDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { canFin, canOps, isAdmin } = useMarketRoles();
  const [t, setT] = useState(null);
  const [edit, setEdit] = useState(false);
  const [share, setShare] = useState(false);
  const [params, setParams] = useSearchParams();
  const [send, setSend] = useState(params.get("send") === "1");
  const [entry, setEntry] = useState({ kind: null, initial: null });
  const [receipt, setReceipt] = useState(null);
  const load = () => api.get(`/market/trips/${id}`).then((r) => setT(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!t) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const locked = t.status === "Settled";
  const act = async (fn, msg) => { try { const { data } = await fn(); if (data?.id) setT({ ...t, ...data }); else load(); toast.success(msg); } catch (e) { toast.error(errMsg(e)); } };
  const replaceDoc = (field, fid) => act(() => api.put(`/market/trips/${t.id}`, { ...t, [field]: fid, override_expired: false }), "Document updated");
  const delEntry = (kind, x) => window.confirm("Delete this entry?") && act(() => api.delete(kind === "payments" ? `/market/payments/${x.id}` : `/market/trips/${t.id}/${kind}/${x.id}`), "Entry deleted");
  const setStatus = (status) => act(() => api.post(`/market/trips/${t.id}/status`, { status }), `Marked ${status}`);
  const delTrip = async () => {
    if (!window.confirm("Delete this market trip?")) return;
    try { await api.delete(`/market/trips/${t.id}`); toast.success("Trip deleted"); nav("/market/trips"); } catch (e) { toast.error(errMsg(e)); }
  };
  const entryProps = (kind) => ({ locked: locked || !canFin, onEdit: (x) => setEntry({ kind, initial: x }), onDelete: (x) => delEntry(kind, x), testid: kind });
  const addBtn = (kind, label) => canFin && !locked && <Button size="sm" variant="outline" data-testid={`add-${kind}-btn`} onClick={() => setEntry({ kind, initial: null })}><Plus className="mr-1 h-3.5 w-3.5" />{label}</Button>;
  return (
    <div className="space-y-5" data-testid="market-trip-detail">
      <div className="flex flex-wrap items-start gap-3 fade-up">
        <Link to="/market/trips" className="rounded-lg border p-2 hover:bg-secondary" data-testid="back-to-market-trips"><ArrowLeft className="h-4 w-4" /></Link>
        <div className="min-w-0 flex-1">
          <h1 className="flex flex-wrap items-center gap-3 text-3xl font-bold tracking-tight" data-testid="page-title"><span className="font-mono">{t.vehicle_number}</span><TripStatus status={t.status} testid="market-trip-status" /></h1>
          <p className="mt-1 text-sm text-muted-foreground">LR <Link className="font-semibold text-primary hover:underline" to={`/bookings/${t.booking_id}`}>{t.lr_number}</Link> · {t.route} · {fmtDate(t.booking_date)} · Vendor <b>{t.vendor_name}</b></p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="send-to-vendor-btn" onClick={() => setSend(true)}><Send className="mr-1 h-4 w-4" />Send to Vendor</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="market-share-docs-btn" onClick={() => setShare(true)}><Share2 className="mr-1 h-4 w-4" />Share All Documents</Button>
          {(canOps || canFin) && !locked && <Button variant="outline" data-testid="edit-market-trip-btn" onClick={() => setEdit(true)}><Pencil className="mr-1 h-4 w-4" />Edit</Button>}
          {canFin && t.status === "Active" && <Button variant="outline" data-testid="mark-pod-verified-btn" onClick={() => setStatus("POD Verified")}><CheckCircle2 className="mr-1 h-4 w-4" />POD Verified</Button>}
          {canFin && t.status === "POD Verified" && <>
            <Button variant="outline" data-testid="reopen-active-btn" onClick={() => setStatus("Active")}>Back to Active</Button>
            <Button data-testid="settle-trip-btn" onClick={() => window.confirm("Settle & close this vendor ledger? Editing will be locked.") && setStatus("Settled")}>Settle & Close</Button>
          </>}
          {isAdmin && <Button variant="ghost" size="icon" data-testid="delete-market-trip-btn" onClick={delTrip}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
        </div>
      </div>
      {t.compliance === "pending_approval" && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300" data-testid="compliance-pending-banner">
          <ShieldAlert className="h-5 w-5" /><span className="flex-1"><b>{t.expired_docs.join(" / ")}</b> expired — admin approval pending (requested by {t.compliance_requested_by}).</span>
          {isAdmin && <Button size="sm" className="bg-rose-600 hover:bg-rose-700" data-testid="approve-compliance-btn" onClick={() => act(() => api.post(`/market/trips/${t.id}/approve-compliance`), "Approved")}>Approve</Button>}
        </div>
      )}
      {t.compliance === "approved" && <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300" data-testid="compliance-approved-banner">Admin override: {t.expired_docs.join(" / ")} expired — approved by {t.compliance_by}.</div>}
      {locked && <div className="rounded-xl border bg-secondary/60 p-3 text-xs" data-testid="settled-banner">Settled on {fmtDate(t.settled_at)} by {t.settled_by} — ledger closed, editing locked.</div>}
      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="Vehicle & Driver Documents" testid="market-docs-card">
          <div className="mb-3 grid grid-cols-3 gap-2 text-xs">
            {[["Driver", t.driver_name || "-"], ["Mobile", t.driver_phone || "-"], ["DL No.", t.dl_number || "-"]].map(([l, v]) => <div key={l} className="rounded-lg bg-secondary/60 p-2"><div className="label-xs">{l}</div><div className="mt-0.5 font-medium">{v}</div></div>)}
          </div>
          <DocsCard t={t} canEdit={canOps && !locked} onReplace={replaceDoc} />
          <DocHistory entity="market_trip" refId={t.id} reloadKey={t.updated_at || JSON.stringify(t).length} />
        </Card>
        {canFin && <FinanceCard t={t} locked={locked} onEdit={() => setEdit(true)} />}
        {canFin && <VendorBillCard t={t} onChange={(vb) => setT({ ...t, vendor_bill: vb })} />}
        {canFin && <Card title="Shortage / Loss Debit Notes" testid="shortages-card" action={addBtn("shortages", "Add Shortage")}>
          <EntryRows items={t.shortages} {...entryProps("shortages")} render={(x) => <><div className="flex justify-between gap-2"><span className="font-medium">{x.remarks}</span><span className="amount text-rose-600">− {inr(x.amount, 2)}</span></div><div className="text-xs text-muted-foreground">{fmtDate(x.date)}{x.file_id && <button className="ml-2 font-semibold text-primary" onClick={() => openFile(x.file_id)}>View proof</button>}</div></>} />
        </Card>}
        {canFin && <Card title="Detention / Halt Charges" testid="detentions-card" action={addBtn("detentions", "Add Detention")}>
          <EntryRows items={t.detentions} {...entryProps("detentions")} render={(x) => <><div className="flex justify-between gap-2"><span className="font-medium">{x.kind} · {x.units} {x.unit_type} × {inr(x.rate)}</span><span className="amount text-emerald-600">+ {inr(x.amount, 2)}</span></div><div className="text-xs text-muted-foreground">{x.reason}</div></>} />
        </Card>}
        {canFin && <Card title="Vendor Payments (Advance + Balance)" testid="payments-card" action={addBtn("payments", "Add Payment")}>
          <EntryRows items={t.payments} {...entryProps("payments")} render={(x) => <><div className="flex justify-between gap-2"><span className="font-medium">{x.kind === "advance" ? "Advance" : "Final Balance"} · {x.mode}</span><span className="amount">{inr(x.amount, 2)}</span></div><div className="flex items-center gap-2 text-xs text-muted-foreground"><span>{fmtDate(x.date)}{x.ref_id && ` · Ref ${x.ref_id}`}{x.note && ` · ${x.note}`}</span><button type="button" data-testid={`payment-receipt-${x.id}`} onClick={() => setReceipt(x.id)} className="inline-flex items-center gap-1 font-semibold text-emerald-600 hover:underline"><MessageCircle className="h-3.5 w-3.5" />Receipt</button></div></>} />
        </Card>}
        {t.vendor && <Card title="Vendor" testid="market-vendor-card">
          <div className="grid grid-cols-2 gap-2 text-sm">
            {[["Name", t.vendor.name], ["Category", t.vendor.category], ["Mobile", t.vendor.phone], ["PAN", t.vendor.pan_number], ["Bank", `${t.vendor.bank_name} · ${t.vendor.account_number}`], ["IFSC", t.vendor.ifsc], ["194C Declaration", t.vendor.declaration_valid ? `Valid till ${fmtDate(t.vendor.declaration_valid_till)}` : "Not valid"]].map(([l, v]) => <div key={l}><div className="label-xs">{l}</div><div className="font-medium">{v}</div></div>)}
          </div>
        </Card>}
      </div>
      <TripDialog open={edit} initial={t} onClose={() => setEdit(false)} onSaved={(d) => { setEdit(false); setT({ ...t, ...d }); }} />
      <ShareDialog t={t} open={share} onClose={() => setShare(false)} />
      <SendVendorDialog t={t} open={send} onClose={() => { setSend(false); if (params.get("send")) setParams({}, { replace: true }); }} />
      <EntryDialog kind={entry.kind} initial={entry.initial} trip={t} onClose={() => setEntry({ kind: null, initial: null })} onSaved={(d) => {
        if (entry.kind === "payments" && !entry.initial) {
          const old = new Set(t.payments.map((x) => x.id));
          const added = d.payments.find((x) => !old.has(x.id));
          if (added) setReceipt(added.id);
        }
        setEntry({ kind: null, initial: null }); setT({ ...t, ...d });
      }} />
      <ReceiptDialog pid={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}
